import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { modifyExpat } from './queries';
import { useMutation } from '@apollo/client';
import { getInputs } from './content';
import { IExpatInfoDrawerProps } from '.';
import { getError } from '@utils/graph-utils';
import { isEmpty } from '@utils/validationUtils';
import { useParams } from 'react-router-dom';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';

const ExpatDetailsDrawer: React.FC<IExpatInfoDrawerProps> = ({
  open,
  onClose,
  onSuccess,
  data,
  lovs,
}) => {
  const params = useParams();
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();
  const [saveExpatDetails] = useMutation(modifyExpat());

  const submitForm = async (values: Record<string, string>) => {
    const [formData] = normalizeDynamicFormDrawerValues(
      getInputs(lovs, data),
      values
    );
    setFormDisabled(true);
    setSubmitButtonState('loading');
    try {
      const variables = {
        entityId: params.id,
        expatInput: {
          medicalClass: formData.medicalClass,
          continuity: formData.continuity,
          continuityNumber: formData.continuityNumber,
          exclusion: formData.exclusion,
          remarks: formData.remarks,
          emergency: formData.emergency,
        },
      };

      const { errors } = await saveExpatDetails({ variables });
      if (isEmpty(errors)) {
        toast.success(
          <ToastSuccessMessage>
            Expat details updated successfully!
          </ToastSuccessMessage>
        );
        setSubmitButtonState('success');
        onSuccess();
        onClose();
      } else {
        setSubmitButtonState(undefined);
        const errorMessage = getError({ errors });
        toast.error(<ToastErrorMessage>{errorMessage}</ToastErrorMessage>);
      }
    } catch (error) {
      const errorMessage = getError({ error });
      toast.error(<ToastErrorMessage>{errorMessage}</ToastErrorMessage>);
      setSubmitButtonState(undefined);
    } finally {
      setFormDisabled(false);
    }
  };

  return (
    <DynamicFormDrawer
      title="Modify Expat Details"
      isOpen={!!open}
      onClose={onClose}
      loading={!data}
      sections={getInputs(lovs, data)}
      onSubmit={submitForm}
      submitButtonState={submitButtonState}
      disableForm={formDisabled}
    />
  );
};

export default ExpatDetailsDrawer;
